// Form Section Variants Configuration
// Thank You, Rich Text Editor, Para Text, Learn from Leaders, Referral Form, Influencer Form, Mobile Lead Form
//
// MANDATORY when adding a variant here: give it a human-readable `name`. It is
// spread into SECTION_VARIANTS by ./index.ts and read through
// `getVariantDetails()` by the page builder and by **Widget Management**
// (`/admin/widget-management`), where that string is the only label identifying
// the widget before someone deletes it from live pages. Miss it and the screen
// falls back to the raw id.
//
// Full checklist for a new section or variant: top of ../sectionTypes.ts.

import {
  setThankYou1,
  setThankYou1009,
  setRichTextEditor1,
  setRichTextEditor6001,
  setBlogWithLead1,
  setBlogInlineImage1,
  setParaText1,
  setHeading1,
  setLearnFromLeaders,
  setLearnFromLeaders1003,
  setReferralForm1,
  setReferralForm1008,
  setReferralForm1048,
  setInfluencerForm1,
  setInfluencerForm1007,
  setInfluencerForm1049,
  setGrievanceForm3000,
  setMobileLeadForm1,
  setMobileLeadForm2,
  setMobileLeadForm6,
} from "@/store/slices/page";
import { Form_1, SegmentWorkbenchForm } from "@/components/organisms/admin/forms";
import type { PageState } from "@/store/slices/page";
import { THANK_YOU_1_CONSTANTS } from "../fields/thankYou/thankYou_1";
import { THANK_YOU_1009_CONSTANTS } from "../fields/thankYou/thankYou_1009";
import { RICH_TEXT_EDITOR_1_CONSTANTS } from "../fields/richTextEditor/richTextEditor_1";
import { RICH_TEXT_EDITOR_6001_CONSTANTS } from "../fields/richTextEditor/richTextEditor_6001";
import { BLOG_WITH_LEAD_1_CONSTANTS } from "../fields/blogWithLead/blog_with_lead_1";
import { BLOG_INLINE_IMAGE_1_CONSTANTS } from "../fields/blogInlineImage/blog_inline_image_1";
import { PARA_TEXT_1_CONSTANTS } from "../fields/paraText/paraText_1";
import { HEADING_1_CONSTANTS } from "../fields/heading/heading_1";
import { LEARN_FROM_LEADERS_1_CONSTANTS } from "../fields/learnFromLeaders/learn_from_leaders_1";
import {
  LEARN_FROM_LEADERS_1003_CONSTANTS,
  LFL_1003_DEFAULT_TITLE,
  LFL_1003_DEFAULT_TITLE_COLOR,
  LFL_1003_DEFAULT_TITLE_ACCENT_COLOR,
  LFL_1003_DEFAULT_DESCRIPTION_COLOR,
  LFL_1003_DEFAULT_CTA_COLOR,
  LFL_1003_DEFAULT_CTA_TEXT_COLOR,
} from "../fields/learnFromLeaders/learn_from_leaders_1003";
import { REFERRAL_FORM_1_CONSTANTS } from "../fields/referralForm/referralForm_1";
import {
  REFERRAL_FORM_1008_CONSTANTS,
  REFERRAL_FORM_1008_DEFAULT_FORM_TITLE,
  REFERRAL_FORM_1008_DEFAULT_PRIMARY_COLOR,
} from "../fields/referralForm/referralForm_1008";
import {
  REFERRAL_FORM_1048_CONSTANTS,
  REFERRAL_FORM_1048_DEFAULT_BACKGROUND_IMAGE,
  REFERRAL_FORM_1048_DEFAULT_FORM_TITLE,
  REFERRAL_FORM_1048_DEFAULT_SIDE_IMAGE,
  REFERRAL_FORM_1048_BUTTON_GRADIENT_END,
  REFERRAL_FORM_1048_BUTTON_GRADIENT_START,
  REFERRAL_FORM_1048_HEADER_GRADIENT_ANGLE,
  REFERRAL_FORM_1048_HEADER_GRADIENT_END,
  REFERRAL_FORM_1048_HEADER_GRADIENT_START,
} from "../fields/referralForm/referralForm_1048";
import { INFLUENCER_FORM_1_CONSTANTS } from "../fields/influencerForm/influencerForm_1";
import {
  INFLUENCER_FORM_1007_CONSTANTS,
  INFLUENCER_FORM_1007_DEFAULT_FORM_TITLE,
  INFLUENCER_FORM_1007_DEFAULT_PRIMARY_COLOR,
} from "../fields/influencerForm/influencerForm_1007";
import {
  INFLUENCER_FORM_1049_CONSTANTS,
  INFLUENCER_FORM_1049_DEFAULT_BACKGROUND_IMAGE,
  INFLUENCER_FORM_1049_DEFAULT_FORM_TITLE,
  INFLUENCER_FORM_1049_DEFAULT_SIDE_IMAGE,
  INFLUENCER_FORM_1049_BUTTON_GRADIENT_END,
  INFLUENCER_FORM_1049_BUTTON_GRADIENT_START,
  INFLUENCER_FORM_1049_HEADER_GRADIENT_ANGLE,
  INFLUENCER_FORM_1049_HEADER_GRADIENT_END,
  INFLUENCER_FORM_1049_HEADER_GRADIENT_START,
} from "../fields/influencerForm/influencerForm_1049";
import { GRIEVANCE_FORM_3000_CONSTANTS } from "../fields/grievanceForm/grievanceForm_3000";
import { MOBILE_LEAD_FORM_1_CONSTANTS } from "../fields/mobileLeadForm/mobileLeadForm_1";
import { MOBILE_LEAD_FORM_2_CONSTANTS } from "../fields/mobileLeadForm/mobileLeadForm_2";
import {
  LEADDROP_MOBILE_STRIP_HELPER_TEXT,
  LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS,
  MOBILE_STRIP_LEADDROP_CTA_FIELDS,
} from "../fields/leadDropForm/leadDropForm_segmented";
import { createThankYou, updateThankYou } from "@/api/admin/thankYou";
import {
  createRichTextEditor,
  updateRichTextEditor,
} from "@/api/admin/richTextEditor";
import { createBlogWithLead, updateBlogWithLead } from "@/api/admin/blogWithLead";
import {
  createBlogInlineImage,
  updateBlogInlineImage,
} from "@/api/admin/blogInlineImage";
import { createParaText, updateParaText } from "@/api/admin/paraText";
import { createHeading, updateHeading } from "@/api/admin/heading";
import { getLearnFromLeadersByComponentId } from "@/api/admin/learnFromLeaders";
import {
  createReferralForm,
  updateReferralForm,
} from "@/api/admin/referralForm";
import {
  createInfluencerForm,
  updateInfluencerForm,
} from "@/api/admin/influencerForm";
import {
  createGrievanceForm,
  updateGrievanceForm,
} from "@/api/admin/grievanceForm";
import {
  createMobileLeadForm,
  updateMobileLeadForm,
} from "@/api/admin/mobileLeadForm";
import dynamic from "next/dynamic";
import ThankYou1 from "@/widgets/thankyou/ThankYou1";
import ThankYou1009 from "@/widgets/thankyou/ThankYou1009";
import RichTextEditor1 from "@/widgets/richtexteditor/RichTextEditor1";
import RichTextEditor6001 from "@/widgets/richtexteditor/RichTextEditor6001";
import BlogWithLead1 from "@/widgets/blog/BlogWithLead1";
import BlogInlineImage1 from "@/widgets/blog/BlogInlineImage1";
import ParaText1 from "@/widgets/paratext/ParaText1";
import Heading1 from "@/widgets/heading/Heading1";
import LearnFromLeaders from "@/widgets/visionaries/LearnFromLeaders";
import LearnFromLeaders1003 from "@/widgets/visionaries/LearnFromLeaders1003";

const ReferralForm1 = dynamic(
  () => import("@/widgets/referralform/ReferralForm1")
);

const ReferralForm1008 = dynamic(
  () => import("@/widgets/referralform/ReferralForm1008")
);

const ReferralForm1048 = dynamic(
  () => import("@/widgets/referralform/ReferralForm1048")
);

const InfluencerForm1 = dynamic(
  () => import("@/widgets/influencerform/InfluencerForm1")
);

const InfluencerForm1007 = dynamic(
  () => import("@/widgets/influencerform/InfluencerForm1007")
);

const InfluencerForm1049 = dynamic(
  () => import("@/widgets/influencerform/InfluencerForm1049")
);

const GrievanceForm1 = dynamic(
  () => import("@/widgets/grievanceform/GrievanceForm1")
);

const LeaddropForm1 = dynamic(
  () => import("@/widgets/leaddropform/LeaddropForm1")
);

import {
  SECTION_TYPES,
  THANK_YOU_VARIANTS,
  RICH_TEXT_EDITOR_VARIANTS,
  BLOG_WITH_LEAD_VARIANTS,
  BLOG_INLINE_IMAGE_VARIANTS,
  PARA_TEXT_VARIANTS,
  HEADING_VARIANTS,
  LEARN_FROM_LEADERS_VARIANTS,
  REFERRAL_FORM_VARIANTS,
  INFLUENCER_FORM_VARIANTS,
  GRIEVANCE_FORM_VARIANTS,
  MOBILE_LEAD_FORM_VARIANTS,
} from "../sectionTypes";
import { extractMetadataFields, createLearnFromLeadersComponent, updateLearnFromLeadersComponent } from "@/helpers";
import { getPreviewData } from "@/utils/variantHelpers";
import {
  LFL_DEFAULT_DESCRIPTION,
  LFL_DEFAULT_DESCRIPTION_COLOR,
  LFL_DEFAULT_HEADING_LINE_1,
  LFL_DEFAULT_HEADING_LINE_2,
} from "@/widgets/visionaries/learnFromLeadersCopy";

export const FORM_SECTION_VARIANTS = {
  [SECTION_TYPES.THANK_YOU]: [
    {
      id: THANK_YOU_VARIANTS.THANK_YOU_1,
      name: "Thank You 1",
      description:
        "Thank you page with background image, gradient, and rich text description",
      componentFields: THANK_YOU_1_CONSTANTS.THANK_YOU_FIELDS_CONFIG,
      ...extractMetadataFields(THANK_YOU_1_CONSTANTS),
      showTable: false,
      setAction: setThankYou1,
      formComponent: Form_1,
      previewComponent: ThankYou1,
      selector: (state: PageState) => state.thank_you_1,
      createMethod: createThankYou,
      updateMethod: updateThankYou,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              description: data.description || "",
              desktop_bg_image: data.desktop_bg_image || "",
              mobile_bg_image: data.mobile_bg_image || "",
              gradient_angle: data.gradient_angle || "254deg",
              gradient_start_color:
                data.gradient_start_color || "rgba(0, 0, 0, 0.00)",
              gradient_start_position:
                data.gradient_start_position || "-25.81%",
              gradient_end_color: data.gradient_end_color || "#0A3D5B",
              gradient_end_position: data.gradient_end_position || "78.26%",
              mobile_gradient_angle: data.mobile_gradient_angle || "0deg",
              mobile_gradient_start_color:
                data.mobile_gradient_start_color || "rgba(5, 33, 50, 0.80)",
              mobile_gradient_start_position:
                data.mobile_gradient_start_position || "0%",
              mobile_gradient_end_color:
                data.mobile_gradient_end_color || "rgba(5, 33, 50, 0.80)",
              mobile_gradient_end_position:
                data.mobile_gradient_end_position || "100%",
            },
          },
        };
      },
    },
    {
      id: THANK_YOU_VARIANTS.THANK_YOU_1009,
      name: "Thank You 1009",
      description:
        "CU-styled thank-you screen only — banner artwork with the confirmation copy overlaid, switching by referral / influencer lead source",
      componentFields: THANK_YOU_1009_CONSTANTS.THANK_YOU_FIELDS_CONFIG,
      ...extractMetadataFields(THANK_YOU_1009_CONSTANTS),
      showTable: false,
      setAction: setThankYou1009,
      formComponent: Form_1,
      previewComponent: ThankYou1009,
      selector: (state: PageState) => state.thank_you_1009,
      createMethod: createThankYou,
      updateMethod: updateThankYou,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              logo_image: data.logo_image || "",
              logo_alt: data.logo_alt || "Logo",
              desktop_bg_image: data.desktop_bg_image || "",
              mobile_bg_image: data.mobile_bg_image || "",
              alt_image: data.alt_image || "Thank you background",
              description: data.description || "",
              referral_description: data.referral_description || "",
              influencer_description: data.influencer_description || "",
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.RICH_TEXT_EDITOR]: [
    {
      id: RICH_TEXT_EDITOR_VARIANTS.RICH_TEXT_EDITOR_1,
      name: "Rich Text Editor 1",
      description: "Rich text editor widget with content and status",
      componentFields:
        RICH_TEXT_EDITOR_1_CONSTANTS.RICH_TEXT_EDITOR_FIELDS_CONFIG,
      ...extractMetadataFields(RICH_TEXT_EDITOR_1_CONSTANTS),
      showTable: false,
      setAction: setRichTextEditor1,
      formComponent: Form_1,
      previewComponent: RichTextEditor1,
      selector: (state: PageState) => state.rich_text_editor_1,
      createMethod: createRichTextEditor,
      updateMethod: updateRichTextEditor,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              content: data.content || "",
              background_color: data.background_color || "#FFFFFF",
              enable_padding: data.enable_padding ?? true,
              padding_x: data.padding_x ?? "",
              padding_y: data.padding_y ?? "",
              status: true,
            },
          },
        };
      },
    },
    {
      id: RICH_TEXT_EDITOR_VARIANTS.RICH_TEXT_EDITOR_6001,
      name: "Rich Text Editor 6001 — BDU Auxiliary Body",
      description:
        "Article column with an optional lead form rail (328px, desktop only). The form comes from University Settings → Lead Drop Form",
      componentFields:
        RICH_TEXT_EDITOR_6001_CONSTANTS.RICH_TEXT_EDITOR_FIELDS_CONFIG,
      ...extractMetadataFields(RICH_TEXT_EDITOR_6001_CONSTANTS),
      showTable: false,
      setAction: setRichTextEditor6001,
      formComponent: Form_1,
      previewComponent: RichTextEditor6001,
      selector: (state: PageState) => state.rich_text_editor_6001,
      createMethod: createRichTextEditor,
      updateMethod: updateRichTextEditor,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              content: data.content || "",
              background_color: data.background_color || "#FFFFFF",
              enable_lead_form: data.enable_lead_form ?? false,
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.BLOG_WITH_LEAD]: [
    {
      id: BLOG_WITH_LEAD_VARIANTS.BLOG_WITH_LEAD_1,
      name: "Blog with lead 1",
      description:
        "Blog article with optional embedded lead form (desktop: right column; mobile: CTA + form)",
      componentFields: BLOG_WITH_LEAD_1_CONSTANTS.BLOG_WITH_LEAD_FIELDS_CONFIG,
      ...extractMetadataFields(BLOG_WITH_LEAD_1_CONSTANTS),
      showTable: false,
      setAction: setBlogWithLead1,
      formComponent: Form_1,
      previewComponent: BlogWithLead1,
      selector: (state: PageState) => state.blog_with_lead_1,
      createMethod: createBlogWithLead,
      updateMethod: updateBlogWithLead,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              content: data.content || "",
              background_color: data.background_color || "#FFFFFF",
              enable_padding: data.enable_padding ?? true,
              enable_lead_form: data.enable_lead_form ?? true,
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.BLOG_INLINE_IMAGE]: [
    {
      id: BLOG_INLINE_IMAGE_VARIANTS.BLOG_INLINE_IMAGE_1,
      name: "Blog inline image 1",
      description:
        "Full-width image in the blog article column with optional caption (Atlas)",
      componentFields:
        BLOG_INLINE_IMAGE_1_CONSTANTS.BLOG_INLINE_IMAGE_FIELDS_CONFIG,
      ...extractMetadataFields(BLOG_INLINE_IMAGE_1_CONSTANTS),
      showTable: false,
      setAction: setBlogInlineImage1,
      formComponent: Form_1,
      previewComponent: BlogInlineImage1,
      selector: (state: PageState) => state.blog_inline_image_1,
      createMethod: createBlogInlineImage,
      updateMethod: updateBlogInlineImage,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const radius = Number(data.border_radius_px ?? 8);
        return {
          data: {
            data: {
              image_url: data.image_url || "",
              mobile_image_url: data.mobile_image_url || "",
              alt_text: data.alt_text || "",
              caption: data.caption || "",
              caption_text_color: data.caption_text_color || "#737373",
              caption_alignment: data.caption_alignment || "left",
              border_radius_px: Number.isFinite(radius) ? radius : 8,
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.PARA_TEXT]: [
    {
      id: PARA_TEXT_VARIANTS.PARA_TEXT_1,
      name: "Para Text 1",
      description: "Paragraph block with background, text color, and description",
      componentFields: PARA_TEXT_1_CONSTANTS.PARA_TEXT_FIELDS_CONFIG,
      ...extractMetadataFields(PARA_TEXT_1_CONSTANTS),
      showTable: false,
      setAction: setParaText1,
      formComponent: Form_1,
      previewComponent: ParaText1,
      selector: (state: PageState) => state.para_text_1,
      createMethod: createParaText,
      updateMethod: updateParaText,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              description: data.description || "",
              desktop_background_color:
                data.desktop_background_color || "#FFFFFF",
              mobile_background_color:
                data.mobile_background_color || "#FFFFFF",
              backvariant: data.backvariant || "variant_3",
              gradient_color_1: data.gradient_color_1 || "#00D4EB",
              gradient_color_2: data.gradient_color_2 || "#000000",
              gradient_color_3: data.gradient_color_3 || "#000000",
              gradient_color_4: data.gradient_color_4 || "#5345C3",
              text_color: data.text_color || "#333333",
              status: true,
              enable_animation: data.enable_animation || false,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.HEADING]: [
    {
      id: HEADING_VARIANTS.HEADING_1,
      name: "Heading 1",
      description:
        "Configurable heading: tag (h1-h6), font family, size, weight, line height, colors",
      componentFields: HEADING_1_CONSTANTS.HEADING_FIELDS_CONFIG,
      ...extractMetadataFields(HEADING_1_CONSTANTS),
      showTable: false,
      setAction: setHeading1,
      formComponent: Form_1,
      previewComponent: Heading1,
      selector: (state: PageState) => state.heading_1,
      createMethod: createHeading,
      updateMethod: updateHeading,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              content: data.content || "",
              heading_tag: data.heading_tag || "h2",
              font_family:
                data.font_family || "var(--font-poppins), Poppins, sans-serif",
              desktop_font_size: data.desktop_font_size || "32",
              mobile_font_size: data.mobile_font_size || "24",
              font_weight: data.font_weight || "600",
              line_height: data.line_height || "1.2",
              text_color: data.text_color || "#111111",
              background_color: data.background_color || "#FFFFFF",
              word_break:
                data.word_break !== undefined ? data.word_break : true,
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.LEARN_FROM_LEADERS]: [
    {
      id: LEARN_FROM_LEADERS_VARIANTS.LEARN_FROM_LEADERS_1,
      name: "Learn from Leaders",
      description: "Headline, description, and auto-scrolling leader cards",
      componentFields: LEARN_FROM_LEADERS_1_CONSTANTS.LEARN_FROM_LEADERS_FIELDS_CONFIG,
      ...extractMetadataFields(LEARN_FROM_LEADERS_1_CONSTANTS),
      showTable: true,
      setAction: setLearnFromLeaders,
      formComponent: Form_1,
      previewComponent: LearnFromLeaders,
      selector: (state: PageState) => state.learn_from_leaders,
      createMethod: createLearnFromLeadersComponent,
      updateMethod: updateLearnFromLeadersComponent,
      getMethod: getLearnFromLeadersByComponentId,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const persisted =
          section?.data && typeof section.data === "object" ? section.data : {};
        const fromLiveProfiles = Array.isArray(data.profiles) && data.profiles.length > 0;
        const fromLiveMetadata = Array.isArray(data.metadata) && data.metadata.length > 0;
        const profiles = fromLiveProfiles
          ? data.profiles
          : fromLiveMetadata
            ? data.metadata
            : Array.isArray(persisted.profiles) && persisted.profiles.length > 0
              ? persisted.profiles
              : Array.isArray(persisted.metadata) && persisted.metadata.length > 0
                ? persisted.metadata
                : [];
        return {
          data: {
            data: {
              pre_title: data.pre_title || LFL_DEFAULT_HEADING_LINE_1,
              pre_title_color: data.pre_title_color || "#FFFFFF",
              title: data.title || LFL_DEFAULT_HEADING_LINE_2,
              title_start_color: data.title_start_color || "#FFFFFF",
              title_end_color: data.title_end_color || "#02A7B6",
              description: data.description || LFL_DEFAULT_DESCRIPTION,
              description_color: data.description_color || LFL_DEFAULT_DESCRIPTION_COLOR,
              desktop_background_color: data.desktop_background_color || "#000000",
              mobile_background_color: data.mobile_background_color || "#000000",
              status: true,
              profiles,
              enable_animation: Boolean(
                data.enable_animation === true || data.enable_animation === 1,
              ),
            },
          },
        };
      },
    },
    {
      id: LEARN_FROM_LEADERS_VARIANTS.LEARN_FROM_LEADERS_1003,
      name: "Learn from Leaders 1003 — Leader cards",
      description:
        "Learn from Leaders 1003 — centered title/description over an optional background image, with leader cards (photo, name, bio, company logos); migrated from the Chandigarh Leadership section.",
      componentFields:
        LEARN_FROM_LEADERS_1003_CONSTANTS.LEARN_FROM_LEADERS_1003_FIELDS_CONFIG,
      ...extractMetadataFields(LEARN_FROM_LEADERS_1003_CONSTANTS),
      showTable: true,
      setAction: setLearnFromLeaders1003,
      formComponent: Form_1,
      previewComponent: LearnFromLeaders1003,
      selector: (state: PageState) => state.learn_from_leaders_1003,
      createMethod: createLearnFromLeadersComponent,
      updateMethod: updateLearnFromLeadersComponent,
      getMethod: getLearnFromLeadersByComponentId,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const persisted =
          section?.data && typeof section.data === "object" ? section.data : {};
        const fromLiveProfiles = Array.isArray(data.profiles) && data.profiles.length > 0;
        const fromLiveMetadata = Array.isArray(data.metadata) && data.metadata.length > 0;
        const profiles = fromLiveProfiles
          ? data.profiles
          : fromLiveMetadata
            ? data.metadata
            : Array.isArray(persisted.profiles) && persisted.profiles.length > 0
              ? persisted.profiles
              : Array.isArray(persisted.metadata) && persisted.metadata.length > 0
                ? persisted.metadata
                : [];
        return {
          data: {
            data: {
              title: data.title || LFL_1003_DEFAULT_TITLE,
              title_start_color:
                data.title_start_color || LFL_1003_DEFAULT_TITLE_COLOR,
              title_end_color:
                data.title_end_color || LFL_1003_DEFAULT_TITLE_ACCENT_COLOR,
              description: data.description || "",
              description_color:
                data.description_color || LFL_1003_DEFAULT_DESCRIPTION_COLOR,
              background_image: data.background_image || "",
              background_image_alt: data.background_image_alt || "",
              cta_text: data.cta_text || "",
              cta_color: data.cta_color || LFL_1003_DEFAULT_CTA_COLOR,
              cta_text_color:
                data.cta_text_color || LFL_1003_DEFAULT_CTA_TEXT_COLOR,
              status: true,
              profiles,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.REFERRAL_FORM]: [
    {
      id: REFERRAL_FORM_VARIANTS.REFERRAL_FORM_1,
      name: "Referral Form 1",
      description:
        "Referral form with banner image, title, description, and form fields",
      componentFields: REFERRAL_FORM_1_CONSTANTS.REFERRAL_FORM_FIELDS_CONFIG,
      ...extractMetadataFields(REFERRAL_FORM_1_CONSTANTS),
      showTable: false,
      setAction: setReferralForm1,
      formComponent: Form_1,
      previewComponent: ReferralForm1,
      selector: (state: PageState) => state.referral_form_1,
      createMethod: createReferralForm,
      updateMethod: updateReferralForm,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              desktop_image: data.desktop_image || "",
              mobile_image: data.mobile_image || "",
              gradient_angle: data.gradient_angle || "254deg",
              gradient_start_color:
                data.gradient_start_color || "rgba(0, 0, 0, 0.00)",
              gradient_start_position:
                data.gradient_start_position || "-25.81%",
              gradient_end_color: data.gradient_end_color || "#0A3D5B",
              gradient_end_position: data.gradient_end_position || "78.26%",
              mobile_gradient_angle: data.mobile_gradient_angle || "0deg",
              mobile_gradient_start_color:
                data.mobile_gradient_start_color || "rgba(5, 33, 50, 0.80)",
              mobile_gradient_start_position:
                data.mobile_gradient_start_position || "0%",
              mobile_gradient_end_color:
                data.mobile_gradient_end_color || "rgba(5, 33, 50, 0.80)",
              mobile_gradient_end_position:
                data.mobile_gradient_end_position || "100%",
              banner_title: data.banner_title || "",
              form_header_background_color:
                data.form_header_background_color || "#EDF1F4",
              form_title_text: data.form_title_text || "",
              form_title_color: data.form_title_color || "#0A3D5B",
              form_description_text: data.form_description_text || "",
              form_description_color: data.form_description_color || "#000000",
              button_text: data.button_text || "Submit",
              button_text_color: data.button_text_color || "#FFFFFF",
              button_color: data.button_color || "#0A3D5B",
              verify_text: data.verify_text || "Verify",
              verify_text_color: data.verify_text_color || "#FFFFFF",
              enter_otp_title: data.enter_otp_title || "Enter OTP",
              enter_otp_title_color: data.enter_otp_title_color || "#0A3D5B",
              otp_sent_text_color: data.otp_sent_text_color || "#0A3D5B",
              edit_text_color: data.edit_text_color || "#F74958",
              resend_text_color: data.resend_text_color || "#0A3D5B",
            },
          },
        };
      },
    },
    {
      id: REFERRAL_FORM_VARIANTS.REFERRAL_FORM_1008,
      name: "Referral Form 1008",
      description:
        "CU-styled referral form (refurl) — name, email and phone with OTP verification, banner background and inline thank-you state",
      componentFields: REFERRAL_FORM_1008_CONSTANTS.REFERRAL_FORM_FIELDS_CONFIG,
      ...extractMetadataFields(REFERRAL_FORM_1008_CONSTANTS),
      showTable: false,
      setAction: setReferralForm1008,
      formComponent: Form_1,
      previewComponent: ReferralForm1008,
      selector: (state: PageState) => state.referral_form_1008,
      createMethod: createReferralForm,
      updateMethod: updateReferralForm,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              logo_image: data.logo_image || "/images/influencer_logo.webp",
              logo_alt: data.logo_alt || "Logo",
              desktop_image:
                data.desktop_image || "/images/influencer_bg_desktop.webp",
              mobile_image:
                data.mobile_image || "/images/influencer_bg_mobile.webp",
              thank_you_mobile_image:
                data.thank_you_mobile_image ||
                "/images/influencer_thanks_bg_mobile.webp",
              alt_image: data.alt_image || "Referral form background",
              banner_title: data.banner_title || "",
              form_header_background_color:
                data.form_header_background_color ||
                REFERRAL_FORM_1008_DEFAULT_PRIMARY_COLOR,
              form_title_text:
                data.form_title_text || REFERRAL_FORM_1008_DEFAULT_FORM_TITLE,
              form_title_color: data.form_title_color || "#FFFFFF",
              button_text: data.button_text || "Submit",
              button_text_color: data.button_text_color || "#FFFFFF",
              button_color:
                data.button_color || REFERRAL_FORM_1008_DEFAULT_PRIMARY_COLOR,
              resend_text_color:
                data.resend_text_color ||
                REFERRAL_FORM_1008_DEFAULT_PRIMARY_COLOR,
            },
          },
        };
      },
    },
    {
      id: REFERRAL_FORM_VARIANTS.REFERRAL_FORM_1048,
      name: "Referral Form 1048",
      description:
        "BDU-styled referral form (refurl) — name, email and phone with OTP verification, white logo bar, background strip with side artwork and a red gradient form header",
      componentFields: REFERRAL_FORM_1048_CONSTANTS.REFERRAL_FORM_FIELDS_CONFIG,
      ...extractMetadataFields(REFERRAL_FORM_1048_CONSTANTS),
      showTable: false,
      setAction: setReferralForm1048,
      formComponent: Form_1,
      previewComponent: ReferralForm1048,
      selector: (state: PageState) => state.referral_form_1048,
      createMethod: createReferralForm,
      updateMethod: updateReferralForm,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              logo_image: data.logo_image || "/images/influencer_logo.webp",
              logo_alt: data.logo_alt || "Logo",
              desktop_image:
                data.desktop_image ||
                REFERRAL_FORM_1048_DEFAULT_BACKGROUND_IMAGE,
              mobile_image:
                data.mobile_image ||
                REFERRAL_FORM_1048_DEFAULT_BACKGROUND_IMAGE,
              side_image:
                data.side_image || REFERRAL_FORM_1048_DEFAULT_SIDE_IMAGE,
              alt_image: data.alt_image || "Referral form background",
              banner_title: data.banner_title || "",
              gradient_angle:
                data.gradient_angle ||
                REFERRAL_FORM_1048_HEADER_GRADIENT_ANGLE,
              gradient_start_color:
                data.gradient_start_color ||
                REFERRAL_FORM_1048_HEADER_GRADIENT_START,
              gradient_end_color:
                data.gradient_end_color ||
                REFERRAL_FORM_1048_HEADER_GRADIENT_END,
              form_title_text:
                data.form_title_text || REFERRAL_FORM_1048_DEFAULT_FORM_TITLE,
              form_title_color: data.form_title_color || "#FFFFFF",
              button_text: data.button_text || "Submit",
              button_text_color: data.button_text_color || "#FFFFFF",
              button_color:
                data.button_color || REFERRAL_FORM_1048_BUTTON_GRADIENT_START,
              button_color_2:
                data.button_color_2 || REFERRAL_FORM_1048_BUTTON_GRADIENT_END,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.INFLUENCER_FORM]: [
    {
      id: INFLUENCER_FORM_VARIANTS.INFLUENCER_FORM_1,
      name: "Influencer Form 1",
      description:
        "Influencer form with banner image, title, description, and form fields",
      componentFields:
        INFLUENCER_FORM_1_CONSTANTS.INFLUENCER_FORM_FIELDS_CONFIG,
      ...extractMetadataFields(INFLUENCER_FORM_1_CONSTANTS),
      showTable: false,
      setAction: setInfluencerForm1,
      formComponent: Form_1,
      previewComponent: InfluencerForm1,
      selector: (state: PageState) => state.influencer_form_1,
      createMethod: createInfluencerForm,
      updateMethod: updateInfluencerForm,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              desktop_image: data.desktop_image || "",
              mobile_image: data.mobile_image || "",
              gradient_angle: data.gradient_angle || "254deg",
              gradient_start_color:
                data.gradient_start_color || "rgba(0, 0, 0, 0.00)",
              gradient_start_position:
                data.gradient_start_position || "-25.81%",
              gradient_end_color: data.gradient_end_color || "#0A3D5B",
              gradient_end_position: data.gradient_end_position || "78.26%",
              mobile_gradient_angle: data.mobile_gradient_angle || "0deg",
              mobile_gradient_start_color:
                data.mobile_gradient_start_color || "rgba(5, 33, 50, 0.80)",
              mobile_gradient_start_position:
                data.mobile_gradient_start_position || "0%",
              mobile_gradient_end_color:
                data.mobile_gradient_end_color || "rgba(5, 33, 50, 0.80)",
              mobile_gradient_end_position:
                data.mobile_gradient_end_position || "100%",
              banner_title: data.banner_title || "",
              form_header_background_color:
                data.form_header_background_color || "#EDF1F4",
              form_title_text: data.form_title_text || "",
              form_title_color: data.form_title_color || "#0A3D5B",
              form_description_text: data.form_description_text || "",
              form_description_color: data.form_description_color || "#000000",
              button_text: data.button_text || "Submit",
              button_text_color: data.button_text_color || "#FFFFFF",
              button_color: data.button_color || "#0A3D5B",
              resend_text_color: data.resend_text_color || "#0A3D5B",
            },
          },
        };
      },
    },
    {
      id: INFLUENCER_FORM_VARIANTS.INFLUENCER_FORM_1007,
      name: "Influencer Form 1007",
      description:
        "CU-styled influencer form with banner background, solid form header bar, filled inputs, and inline thank-you state",
      componentFields:
        INFLUENCER_FORM_1007_CONSTANTS.INFLUENCER_FORM_FIELDS_CONFIG,
      ...extractMetadataFields(INFLUENCER_FORM_1007_CONSTANTS),
      showTable: false,
      setAction: setInfluencerForm1007,
      formComponent: Form_1,
      previewComponent: InfluencerForm1007,
      selector: (state: PageState) => state.influencer_form_1007,
      createMethod: createInfluencerForm,
      updateMethod: updateInfluencerForm,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              logo_image: data.logo_image || "/images/influencer_logo.webp",
              logo_alt: data.logo_alt || "Logo",
              desktop_image:
                data.desktop_image || "/images/influencer_bg_desktop.webp",
              mobile_image:
                data.mobile_image || "/images/influencer_bg_mobile.webp",
              thank_you_mobile_image:
                data.thank_you_mobile_image ||
                "/images/influencer_thanks_bg_mobile.webp",
              alt_image: data.alt_image || "Influencer form background",
              banner_title: data.banner_title || "",
              form_header_background_color:
                data.form_header_background_color ||
                INFLUENCER_FORM_1007_DEFAULT_PRIMARY_COLOR,
              form_title_text:
                data.form_title_text || INFLUENCER_FORM_1007_DEFAULT_FORM_TITLE,
              form_title_color: data.form_title_color || "#FFFFFF",
              button_text: data.button_text || "Submit",
              button_text_color: data.button_text_color || "#FFFFFF",
              button_color:
                data.button_color || INFLUENCER_FORM_1007_DEFAULT_PRIMARY_COLOR,
              resend_text_color:
                data.resend_text_color ||
                INFLUENCER_FORM_1007_DEFAULT_PRIMARY_COLOR,
            },
          },
        };
      },
    },
    {
      id: INFLUENCER_FORM_VARIANTS.INFLUENCER_FORM_1049,
      name: "Influencer Form 1049",
      description:
        "BDU-styled influencer form — name, email, phone, course and qualification with OTP verification, white logo bar, background strip with side artwork and a red gradient form header",
      componentFields:
        INFLUENCER_FORM_1049_CONSTANTS.INFLUENCER_FORM_FIELDS_CONFIG,
      ...extractMetadataFields(INFLUENCER_FORM_1049_CONSTANTS),
      showTable: false,
      setAction: setInfluencerForm1049,
      formComponent: Form_1,
      previewComponent: InfluencerForm1049,
      selector: (state: PageState) => state.influencer_form_1049,
      createMethod: createInfluencerForm,
      updateMethod: updateInfluencerForm,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              logo_image: data.logo_image || "/images/influencer_logo.webp",
              logo_alt: data.logo_alt || "Logo",
              desktop_image:
                data.desktop_image ||
                INFLUENCER_FORM_1049_DEFAULT_BACKGROUND_IMAGE,
              mobile_image:
                data.mobile_image ||
                INFLUENCER_FORM_1049_DEFAULT_BACKGROUND_IMAGE,
              side_image:
                data.side_image || INFLUENCER_FORM_1049_DEFAULT_SIDE_IMAGE,
              alt_image: data.alt_image || "Influencer form background",
              banner_title: data.banner_title || "",
              gradient_angle:
                data.gradient_angle ||
                INFLUENCER_FORM_1049_HEADER_GRADIENT_ANGLE,
              gradient_start_color:
                data.gradient_start_color ||
                INFLUENCER_FORM_1049_HEADER_GRADIENT_START,
              gradient_end_color:
                data.gradient_end_color ||
                INFLUENCER_FORM_1049_HEADER_GRADIENT_END,
              form_title_text:
                data.form_title_text || INFLUENCER_FORM_1049_DEFAULT_FORM_TITLE,
              form_title_color: data.form_title_color || "#FFFFFF",
              button_text: data.button_text || "Submit",
              button_text_color: data.button_text_color || "#FFFFFF",
              button_color:
                data.button_color || INFLUENCER_FORM_1049_BUTTON_GRADIENT_START,
              button_color_2:
                data.button_color_2 || INFLUENCER_FORM_1049_BUTTON_GRADIENT_END,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.GRIEVANCE_FORM]: [
    {
      id: GRIEVANCE_FORM_VARIANTS.GRIEVANCE_FORM_1,
      name: "Grievance Form",
      description:
        "Configurable grievance/contact form. Labels, colors, background, recipient and Resend API key are all editable; submissions are emailed via Resend.",
      componentFields: GRIEVANCE_FORM_3000_CONSTANTS.GRIEVANCE_FORM_FIELDS_CONFIG,
      ...extractMetadataFields(GRIEVANCE_FORM_3000_CONSTANTS),
      showTable: false,
      setAction: setGrievanceForm3000,
      formComponent: Form_1,
      previewComponent: GrievanceForm1,
      selector: (state: PageState) => state.grievance_form_3000,
      createMethod: createGrievanceForm,
      updateMethod: updateGrievanceForm,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.MOBILE_LEAD_FORM]: [
    {
      id: MOBILE_LEAD_FORM_VARIANTS.MOBILE_LEAD_FORM_1,
      name: "Mobile Lead Form 1",
      description: "Mobile lead form with status toggle",
      componentFields:
        MOBILE_LEAD_FORM_1_CONSTANTS.MOBILE_LEAD_FORM_FIELDS_CONFIG,
      ...extractMetadataFields(MOBILE_LEAD_FORM_1_CONSTANTS),
      showTable: false,
      setAction: setMobileLeadForm1,
      formComponent: Form_1,
      previewComponent: LeaddropForm1,
      selector: (state: PageState) => state.mobile_lead_form_1,
      createMethod: createMobileLeadForm,
      updateMethod: updateMobileLeadForm,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              status: data.status ?? true,
            },
          },
        };
      },
    },
    {
      id: MOBILE_LEAD_FORM_VARIANTS.MOBILE_LEAD_FORM_2,
      name: "Mobile Lead Form 2",
      description:
        "Mobile lead form with countdown timer, gradient background, and CTA button",
      componentFields:
        MOBILE_LEAD_FORM_2_CONSTANTS.MOBILE_LEAD_FORM_FIELDS_CONFIG,
      ...extractMetadataFields(MOBILE_LEAD_FORM_2_CONSTANTS),
      showTable: false,
      setAction: setMobileLeadForm2,
      formComponent: Form_1,
      previewComponent: null,
      selector: (state: PageState) => state.mobile_lead_form_2,
      createMethod: createMobileLeadForm,
      updateMethod: updateMobileLeadForm,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              title: data.title || "Admission Closed on 02 Feb",
              title_color: data.title_color || "#FFFFFF",
              gradient_start_color: data.gradient_start_color || "#02A7B6",
              gradient_end_color: data.gradient_end_color || "#342B7C",
              gradient_angle: data.gradient_angle || "180deg",
              enable_timer: data.enable_timer ?? true,
              timer_end_date: data.timer_end_date || "",
              timer_end_time: data.timer_end_time || "23:59:59",
              timer_number_color: data.timer_number_color || "#222222",
              timer_text_color: data.timer_text_color || "#222222",
              timer_background_color:
                data.timer_background_color || "rgba(255, 255, 255, 0.8)",
              cta_text: data.cta_text || "Apply Now",
              cta_button_text_color:
                data.cta_button_text_color || "#222222",
              cta_button_color: data.cta_button_color || "#FFFFFF",
              status: true,
            },
          },
        };
      },
    },
    {
      id: MOBILE_LEAD_FORM_VARIANTS.MOBILE_LEAD_FORM_6,
      name: "Mobile lead form 6 — multi-step intake",
      description: "Gradient strip CTA + segment workbench (same as form variant 6)",
      componentFields: LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS.MERGED_FIELDS_CONFIG,
      segmentDefinitions: [...LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS.SEGMENT_WORKBENCH_SEGMENTS],
      globalSegmentFields: [...LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS.LEAD_STRIP_GLOBAL_FIELDS],
      globalLeadDropMobileStripFields: [...MOBILE_STRIP_LEADDROP_CTA_FIELDS],
      globalLeadDropMobileStripHelpText: LEADDROP_MOBILE_STRIP_HELPER_TEXT,
      ...extractMetadataFields(LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS),
      showTable: false,
      setAction: setMobileLeadForm6,
      formComponent: SegmentWorkbenchForm,
      previewComponent: null,
      selector: (state: PageState) => state.mobile_lead_form_6,
      createMethod: createMobileLeadForm,
      updateMethod: updateMobileLeadForm,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              status: data.status ?? true,
            },
          },
        };
      },
    },
  ],
};
